import type { Route, RouteStorageProvider, StorageResult, RoutePoint, RouteFullData } from '../../types/storage.js';
import { handleApiError } from '../errorHandler.js';
import { ErrorCodes } from '../../types/errors.js';

export class SupabaseStorageProvider implements RouteStorageProvider {
  private baseUrl: string;
  private anonKey: string;
  private getAccessToken?: () => Promise<string | null>;

  constructor(
    baseUrl?: string, 
    anonKey?: string, 
    getAccessToken?: () => Promise<string | null>
  ) {
    this.baseUrl = baseUrl || import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
    // Use environment variable for anon key
    this.anonKey = anonKey || import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvY2FsaG9zdCIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzM2NzA1NjAwLCJleHAiOjE4NTIzNjI3MDB9.a2kcrdv2Ydk4yHxB4HTkXUdFna-nt4r-OgQW-Tq8a_k';
    this.getAccessToken = getAccessToken;
  }

  private async getHeadersForMethod(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'apikey': this.anonKey
    };

    // Use the session-aware token retrieval if available
    if (this.getAccessToken) {
      try {
        const accessToken = await this.getAccessToken();
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
        // Note: No warning for missing token as this is expected for anonymous users
      } catch (error) {
        console.warn('❌ Failed to get access token:', error);
        // Fall back to anonymous access
      }
    } else {
      // Fallback: Add authentication header if user is logged in (legacy behavior)
      const session = localStorage.getItem('cycling-buddy-session');
      if (session) {
        try {
          const sessionData = JSON.parse(session);
          if (sessionData.access_token && sessionData.expires_at > Date.now()) {
            headers['Authorization'] = `Bearer ${sessionData.access_token}`;
          }
        } catch (e) {
          console.warn('Failed to parse session data:', e);
        }
      }
    }

    return headers;
  }

  /**
   * Get appropriate error code for storage operations
   */
  private getStorageErrorCode(error: any, operation: 'save' | 'load' | 'delete' | 'update'): string {
    const message = error?.message || error?.toString() || '';
    
    // Check for specific error patterns
    if (message.includes('401') || message.includes('Unauthorized')) {
      return ErrorCodes.API_UNAUTHORIZED;
    }
    
    if (message.includes('403') || message.includes('Forbidden')) {
      return ErrorCodes.API_FORBIDDEN;
    }
    
    if (message.includes('409') || message.includes('duplicate') || message.includes('unique constraint')) {
      return ErrorCodes.VALIDATION_NAME_EXISTS;
    }
    
    if (message.includes('413') || message.includes('too large')) {
      return ErrorCodes.STORAGE_QUOTA_EXCEEDED;
    }
    
    if (message.includes('timeout') || message.includes('ECONNABORTED')) {
      return ErrorCodes.NETWORK_TIMEOUT;
    }
    
    if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
      return ErrorCodes.NETWORK_UNAVAILABLE;
    }
    
    if (message.includes('500') || message.includes('502') || message.includes('503')) {
      return ErrorCodes.API_SERVER_ERROR;
    }
    
    // Operation-specific error codes
    switch (operation) {
      case 'save':
        return ErrorCodes.STORAGE_SAVE_FAILED;
      case 'load':
        return ErrorCodes.STORAGE_LOAD_FAILED;
      case 'delete':
        return ErrorCodes.STORAGE_DELETE_FAILED;
      case 'update':
        return ErrorCodes.STORAGE_SAVE_FAILED; // Update uses same as save
      default:
        return ErrorCodes.API_SERVER_ERROR;
    }
  }

  /**
   * Generate user-friendly error messages for storage operations
   */
  private getStorageErrorMessage(error: any, operation: 'save' | 'load' | 'delete' | 'update'): string {
    const message = error?.message || error?.toString() || '';
    
    // Check for specific error patterns
    if (message.includes('401') || message.includes('Unauthorized')) {
      return 'Authentication required. Please sign in to save your route.';
    }
    
    if (message.includes('403') || message.includes('Forbidden')) {
      return 'You don\'t have permission to perform this action.';
    }
    
    if (message.includes('409') || message.includes('duplicate') || message.includes('unique constraint')) {
      return 'A route with this name already exists. Please choose a different name.';
    }
    
    if (message.includes('413') || message.includes('too large')) {
      return 'Route data is too large. Try reducing the number of waypoints.';
    }
    
    if (message.includes('500') || message.includes('502') || message.includes('503')) {
      return 'Route storage service is temporarily unavailable. Please try again later.';
    }
    
    if (message.includes('timeout') || message.includes('ECONNABORTED')) {
      return 'Operation timed out. Please check your connection and try again.';
    }
    
    if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
      return 'Unable to connect to route storage service. Please check your connection.';
    }
    
    // Operation-specific messages
    switch (operation) {
      case 'save':
        return 'Failed to save route. Please try again.';
      case 'load':
        return 'Failed to load route data. The route may no longer exist.';
      case 'delete':
        return 'Failed to delete route. Please try again.';
      case 'update':
        return 'Failed to update route. Please try again.';
      default:
        return 'Route storage operation failed. Please try again.';
    }
  }

  /**
   * Ensures route name is unique by appending numbers if necessary
   */
  private async ensureUniqueRouteName(proposedName: string, userId: string): Promise<string> {
    // Step 1: Check if the proposed name is already taken
    const isUnique = await this.isRouteNameUnique(proposedName, userId);
    if (isUnique) {
      return proposedName;
    }

    // Step 2: Find a unique variation by appending numbers
    let counter = 1;
    let candidateName: string;
    
    do {
      candidateName = `${proposedName} (${counter})`;
      const isUnique = await this.isRouteNameUnique(candidateName, userId);
      
      if (isUnique) {
        return candidateName;
      }
      
      counter++;
    } while (counter <= 100); // Safety limit to prevent infinite loops

    // Fallback: use timestamp if we somehow hit the limit
    const timestamp = new Date().getTime();
    const fallbackName = `${proposedName} (${timestamp})`;
    console.warn(`Could not find unique name after 100 attempts, using timestamp: ${fallbackName}`);
    return fallbackName;
  }

  /**
   * Check if a route name is unique for a specific user
   */
  private async isRouteNameUnique(name: string, userId: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseUrl}/rest/v1/routes?select=id&name=eq.${encodeURIComponent(name)}&created_by=eq.${userId}`,
        {
          method: 'GET',
          headers: await this.getHeadersForMethod()
        }
      );

      if (!response.ok) {
        console.warn(`Failed to check route name uniqueness: ${response.status}`);
        return false; // Assume not unique if we can't check
      }

      const existingRoutes = await response.json();
      return existingRoutes.length === 0;
    } catch (error) {
      console.error('Error checking route name uniqueness:', error);
      return false; // Assume not unique if error occurs
    }
  }

  async saveRoute(route: Omit<Route, 'id' | 'created'>): Promise<StorageResult<Route>> {
    try {
      // Step 1: Ensure unique route name
      const uniqueName = await this.ensureUniqueRouteName(route.name, route.userId);
      
      // Step 2: Insert the route record
      const headers = await this.getHeadersForMethod();
      const routeResponse = await fetch(`${this.baseUrl}/rest/v1/routes`, {
        method: 'POST',
        headers: {
          ...headers,
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          name: uniqueName, // Use the unique name
          bike_profile: route.bikeProfile,
          created_by: route.userId,
          distance: route.distance || null,
          duration: route.duration || null,
          elevation_gain: route.ascend || null,
          thumbnail_path: route.thumbnail_path || null,
          elevation_thumbnail_path: route.elevation_thumbnail_path || null,
          source_type: route.sourceType || 'manual',
          original_filename: route.originalFilename || null,
          gpx_metadata: route.gpxMetadata || null
        })
      });

      if (!routeResponse.ok) {
        const errorText = await routeResponse.text();
        throw new Error(`Failed to save route: ${routeResponse.status} - ${errorText}`);
      }

      const savedRoutes = await routeResponse.json();
      const savedRoute = savedRoutes[0]; // Supabase returns an array with 'return=representation'

      if (!savedRoute || !savedRoute.id) {
        throw new Error('Route was saved but no ID returned');
      }

      // Step 2: Insert route points
      if (route.points && route.points.length > 0) {
        const routePointsToInsert = route.points.map((point, index) => ({
          route_id: savedRoute.id,
          latitude: point.lat,                    // Database uses latitude
          longitude: point.lng,                   // Database uses longitude
          sequence: point.orderIndex !== undefined ? point.orderIndex : index,
          // Ensure all objects have the same keys by providing null defaults
          address_street: point.address_street || null,
          address_house_number: point.address_house_number || null,
          address_city: point.address_city || null,
          address_suburb: point.address_suburb || null,
          address_country: point.address_country || null,
          address_display_name: point.address_display_name || null,
          address_raw_data: point.address_raw_data || null
        }));

        const pointsResponse = await fetch(`${this.baseUrl}/rest/v1/route_points`, {
          method: 'POST',
          headers: {
            ...(await this.getHeadersForMethod()),
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(routePointsToInsert)
        });

        if (!pointsResponse.ok) {
          const errorText = await pointsResponse.text();
          // If points fail to save, we should clean up the route
          await this.deleteRoute(savedRoute.id);
          throw new Error(`Failed to save route points: ${pointsResponse.status} - ${errorText}`);
        }

        const savedPoints = await pointsResponse.json();
        
        // Convert back to our Route format
        const routeWithPoints: Route = {
          id: savedRoute.id,
          name: savedRoute.name,
          bikeProfile: savedRoute.bike_profile,
          userId: savedRoute.created_by,
          created: new Date(savedRoute.created_at), // Database uses created_at
          updated: new Date(savedRoute.updated_at), // Database uses updated_at
          distance: savedRoute.distance,
          duration: savedRoute.duration,
          ascend: savedRoute.elevation_gain,
          thumbnail_path: savedRoute.thumbnail_path,
          elevation_thumbnail_path: savedRoute.elevation_thumbnail_path,
          points: savedPoints.map((point: any): RoutePoint => ({
            id: point.id,
            lat: point.latitude,  // Database uses latitude
            lng: point.longitude,  // Database uses longitude
            orderIndex: point.sequence,
            // Include address fields
            address_street: point.address_street,
            address_house_number: point.address_house_number,
            address_city: point.address_city,
            address_suburb: point.address_suburb,
            address_country: point.address_country,
            address_display_name: point.address_display_name,
            address_raw_data: point.address_raw_data
          }))
        };

        return {
          success: true,
          data: routeWithPoints
        };
      } else {
        // Route without points
        const routeWithoutPoints: Route = {
          id: savedRoute.id,
          name: savedRoute.name,
          bikeProfile: savedRoute.bike_profile,
          userId: savedRoute.created_by,
          created: new Date(savedRoute.created_at), // Database uses created_at
          updated: new Date(savedRoute.updated_at), // Database uses updated_at
          distance: savedRoute.distance,
          duration: savedRoute.duration,
          ascend: savedRoute.elevation_gain,
          thumbnail_path: savedRoute.thumbnail_path,
          elevation_thumbnail_path: savedRoute.elevation_thumbnail_path,
          points: []
        };

        return {
          success: true,
          data: routeWithoutPoints
        };
      }
    } catch (error) {
      console.error('Failed to save route to Supabase:', error);
      
      // Enhanced error handling with categorization
      const errorCode = this.getStorageErrorCode(error, 'save');
      await handleApiError(error as Error, 'supabase/routes', 'save_route', {
        routeName: route.name,
        pointCount: route.points?.length || 0,
        service: 'storage',
        serviceType: 'Route Storage',
        customMessage: this.getStorageErrorMessage(error, 'save'),
        errorCode
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save route'
      };
    }
  }

  async getRoute(id: string, includeFullData: boolean = false): Promise<StorageResult<Route>> {
    try {
      // Get route with its points
      let selectQuery = '*,route_points(*)';
      
      // Optionally include full route data
      if (includeFullData) {
        selectQuery = '*,route_points(*),route_full_data(*)';
      }
      
      const response = await fetch(
        `${this.baseUrl}/rest/v1/routes?id=eq.${id}&select=${selectQuery}`,
        {
          headers: await this.getHeadersForMethod()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const routes = await response.json();
      
      if (!routes || routes.length === 0) {
        return {
          success: false,
          error: 'Route not found'
        };
      }

      const supabaseRoute = routes[0];
      
      // Convert to our Route format
      const route: Route = {
        id: supabaseRoute.id,
        name: supabaseRoute.name,
        bikeProfile: supabaseRoute.bike_profile,
        userId: supabaseRoute.created_by,
        created: new Date(supabaseRoute.created_at), // Database uses created_at
        updated: new Date(supabaseRoute.updated_at), // Database uses updated_at
        distance: supabaseRoute.distance,
        duration: supabaseRoute.duration,
        ascend: supabaseRoute.elevation_gain,
        thumbnail_path: supabaseRoute.thumbnail_path,
        elevation_thumbnail_path: supabaseRoute.elevation_thumbnail_path,
        isPublic: supabaseRoute.is_public || false, // Include visibility setting
        sourceType: supabaseRoute.source_type || 'manual',
        originalFilename: supabaseRoute.original_filename,
        gpxMetadata: supabaseRoute.gpx_metadata,
        points: (supabaseRoute.route_points || [])
          .sort((a: any, b: any) => a.sequence - b.sequence)
          .map((point: any): RoutePoint => ({
            id: point.id,
            lat: point.latitude,  // Database uses latitude
            lng: point.longitude,  // Database uses longitude
            orderIndex: point.sequence,
            // Include address fields
            address_street: point.address_street,
            address_house_number: point.address_house_number,
            address_city: point.address_city,
            address_suburb: point.address_suburb,
            address_country: point.address_country,
            address_display_name: point.address_display_name,
            address_raw_data: point.address_raw_data
          }))
      };

      // Include full route data if requested and available
      if (includeFullData && supabaseRoute.route_full_data && supabaseRoute.route_full_data.length > 0) {
        const fullData = supabaseRoute.route_full_data[0]; // Should be only one record due to unique constraint
        route.fullRouteData = {
          id: fullData.id,
          route_id: fullData.route_id,
          route_geometry: fullData.route_geometry,
          route_properties: fullData.route_properties,
          routing_engine: fullData.routing_engine,
          routing_profile: fullData.routing_profile,
          routing_version: fullData.routing_version,
          routing_timestamp: fullData.routing_timestamp ? new Date(fullData.routing_timestamp) : undefined,
          coordinates_count: fullData.coordinates_count,
          is_compressed: fullData.is_compressed,
          compression_algorithm: fullData.compression_algorithm,
          created_at: fullData.created_at ? new Date(fullData.created_at) : undefined,
          updated_at: fullData.updated_at ? new Date(fullData.updated_at) : undefined
        };
      }

      return {
        success: true,
        data: route
      };
    } catch (error) {
      console.error('Failed to get route from Supabase:', error);
      
      // Enhanced error handling with categorization
      const errorCode = this.getStorageErrorCode(error, 'load');
      await handleApiError(error as Error, 'supabase/routes', 'load_route', {
        routeId: id,
        service: 'storage',
        serviceType: 'Route Storage',
        customMessage: this.getStorageErrorMessage(error, 'load'),
        errorCode
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load route'
      };
    }
  }

  async getRoutes(_userId: string): Promise<StorageResult<Route[]>> {
    try {
      // Get only routes created by the current user (not all public routes!)
      // RLS policy allows seeing public routes from others, but MyRoutes page should only show user's own routes
      const url = `${this.baseUrl}/rest/v1/routes?select=*,route_points(*)&is_deleted=eq.false&created_by=eq.${_userId}&order=created_at.desc`;
      
      const response = await fetch(url, {
        headers: await this.getHeadersForMethod()
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const supabaseRoutes = await response.json();
      
      // Convert to our Route format
      const routes: Route[] = supabaseRoutes.map((supabaseRoute: any): Route => ({
        id: supabaseRoute.id,
        name: supabaseRoute.name,
        bikeProfile: supabaseRoute.bike_profile,
        userId: supabaseRoute.created_by,
        created: new Date(supabaseRoute.created_at),  // Database uses created_at
        updated: new Date(supabaseRoute.updated_at),  // Database uses updated_at
        distance: supabaseRoute.distance,
        duration: supabaseRoute.duration,
        ascend: supabaseRoute.elevation_gain,
        thumbnail_path: supabaseRoute.thumbnail_path,
        elevation_thumbnail_path: supabaseRoute.elevation_thumbnail_path,
        isPublic: supabaseRoute.is_public || false, // Include visibility setting
        sourceType: supabaseRoute.source_type || 'manual',
        originalFilename: supabaseRoute.original_filename,
        gpxMetadata: supabaseRoute.gpx_metadata,
        points: (supabaseRoute.route_points || [])
          .sort((a: any, b: any) => a.sequence - b.sequence)
          .map((point: any): RoutePoint => ({
            id: point.id,
            lat: point.latitude,           // Database uses latitude
            lng: point.longitude,          // Database uses longitude
            orderIndex: point.sequence,
            // Include address fields
            address_street: point.address_street,
            address_house_number: point.address_house_number,
            address_city: point.address_city,
            address_suburb: point.address_suburb,
            address_country: point.address_country,
            address_display_name: point.address_display_name,
            address_raw_data: point.address_raw_data
          }))
      }));

      return {
        success: true,
        data: routes
      };
    } catch (error) {
      console.error('Failed to get routes from Supabase:', error);
      
      // Enhanced error handling with categorization
      const errorCode = this.getStorageErrorCode(error, 'load');
      await handleApiError(error as Error, 'supabase/routes', 'load_routes', {
        userId: _userId,
        service: 'storage',
        serviceType: 'Route Storage',
        customMessage: this.getStorageErrorMessage(error, 'load'),
        errorCode
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load routes'
      };
    }
  }

  async deleteRoute(id: string): Promise<StorageResult<boolean>> {
    try {
      // Soft delete: set is_deleted=true and deleted_at=NOW()
      const routeResponse = await fetch(
        `${this.baseUrl}/rest/v1/routes?id=eq.${id}`,
        {
          method: 'PATCH',
          headers: {
            ...(await this.getHeadersForMethod()),
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify({
            is_deleted: true,
            deleted_at: new Date().toISOString()
          })
        }
      );

      if (!routeResponse.ok) {
        throw new Error(`Failed to delete route: ${routeResponse.status}`);
      }

      return {
        success: true,
        data: true
      };
    } catch (error) {
      console.error('Failed to delete route from Supabase:', error);
      
      // Enhanced error handling with categorization
      const errorCode = this.getStorageErrorCode(error, 'delete');
      await handleApiError(error as Error, 'supabase/routes', 'delete_route', {
        routeId: id,
        service: 'storage',
        serviceType: 'Route Storage',
        customMessage: this.getStorageErrorMessage(error, 'delete'),
        errorCode
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete route'
      };
    }
  }

  async updateRoute(id: string, route: Partial<Route>): Promise<StorageResult<Route>> {
    try {
      // Prepare update data (only include fields that can be updated)
      const updateData: any = {};
      
      if (route.name !== undefined) {
        updateData.name = route.name;
      }
      if (route.bikeProfile !== undefined) {
        updateData.bike_profile = route.bikeProfile;
      }
      if (route.distance !== undefined) {
        updateData.distance = route.distance;
      }
      if (route.duration !== undefined) {
        updateData.duration = route.duration;
      }
      if (route.ascend !== undefined) {
        updateData.elevation_gain = route.ascend;
      }
      if (route.thumbnail_path !== undefined) {
        updateData.thumbnail_path = route.thumbnail_path;
      }
      if (route.elevation_thumbnail_path !== undefined) {
        updateData.elevation_thumbnail_path = route.elevation_thumbnail_path;
      }
      if (route.isPublic !== undefined) {
        updateData.is_public = route.isPublic;
      }

      // Always update the updated_at timestamp
      updateData.updated_at = new Date().toISOString();

      // Update the route metadata
      const response = await fetch(
        `${this.baseUrl}/rest/v1/routes?id=eq.${id}`,
        {
          method: 'PATCH',
          headers: {
            ...(await this.getHeadersForMethod()),
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update route: ${response.status}`);
      }

      const updatedRoutes = await response.json();
      
      if (!updatedRoutes || updatedRoutes.length === 0) {
        return {
          success: false,
          error: 'Route not found or update failed'
        };
      }

      // Update route points if provided
      if (route.points !== undefined) {
        // First, delete existing route points
        const deleteResponse = await fetch(
          `${this.baseUrl}/rest/v1/route_points?route_id=eq.${id}`,
          {
            method: 'DELETE',
            headers: await this.getHeadersForMethod()
          }
        );

        if (!deleteResponse.ok) {
          throw new Error(`Failed to delete existing route points: ${deleteResponse.status}`);
        }

        // Then, insert new route points if any
        if (route.points && route.points.length > 0) {
          const routePointsToInsert = route.points.map((point, index) => {
            const basePoint: any = {
              route_id: id,
              latitude: point.lat,                    // Database uses latitude
              longitude: point.lng,                   // Database uses longitude
              sequence: point.orderIndex !== undefined ? point.orderIndex : index
            };
            
            // Only include address fields if at least one exists on ANY point
            // This ensures all objects have the same structure
            const hasAddressFields = route.points!.some(p => 
              p.address_street || p.address_house_number || p.address_city || 
              p.address_suburb || p.address_country || p.address_display_name || 
              p.address_raw_data
            );
            
            if (hasAddressFields) {
              basePoint.address_street = point.address_street || null;
              basePoint.address_house_number = point.address_house_number || null;
              basePoint.address_city = point.address_city || null;
              basePoint.address_suburb = point.address_suburb || null;
              basePoint.address_country = point.address_country || null;
              basePoint.address_display_name = point.address_display_name || null;
              basePoint.address_raw_data = point.address_raw_data || null;
            }
            
            return basePoint;
          });

          const pointsResponse = await fetch(`${this.baseUrl}/rest/v1/route_points`, {
            method: 'POST',
            headers: {
              ...(await this.getHeadersForMethod()),
              'Prefer': 'return=representation'
            },
            body: JSON.stringify(routePointsToInsert)
          });

          if (!pointsResponse.ok) {
            const errorText = await pointsResponse.text();
            throw new Error(`Failed to save updated route points: ${pointsResponse.status} - ${errorText}`);
          }
        }
      }

      // Get the complete updated route with points
      return this.getRoute(id);
    } catch (error) {
      console.error('Failed to update route in Supabase:', error);
      
      // Enhanced error handling with categorization
      const errorCode = this.getStorageErrorCode(error, 'update');
      await handleApiError(error as Error, 'supabase/routes', 'update_route', {
        routeId: id,
        service: 'storage',
        serviceType: 'Route Storage',
        customMessage: this.getStorageErrorMessage(error, 'update'),
        errorCode
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update route'
      };
    }
  }

  /**
   * Update a specific route point with new data (e.g., geocoded address)
   */
  async updateRoutePoint(routeId: string, pointId: string, pointData: Partial<RoutePoint>): Promise<StorageResult<RoutePoint>> {
    try {
      // Prepare the update data for database format
      const updateData: any = {};
      
      if (pointData.lat !== undefined) updateData.latitude = pointData.lat;
      if (pointData.lng !== undefined) updateData.longitude = pointData.lng;
      if (pointData.orderIndex !== undefined) updateData.sequence = pointData.orderIndex;
      if (pointData.address_street !== undefined) updateData.address_street = pointData.address_street;
      if (pointData.address_house_number !== undefined) updateData.address_house_number = pointData.address_house_number;
      if (pointData.address_city !== undefined) updateData.address_city = pointData.address_city;
      if (pointData.address_suburb !== undefined) updateData.address_suburb = pointData.address_suburb;
      if (pointData.address_country !== undefined) updateData.address_country = pointData.address_country;
      if (pointData.address_display_name !== undefined) updateData.address_display_name = pointData.address_display_name;
      if (pointData.address_raw_data !== undefined) updateData.address_raw_data = pointData.address_raw_data;

      // Update the route point in the database
      const response = await fetch(
        `${this.baseUrl}/rest/v1/route_points?id=eq.${pointId}&route_id=eq.${routeId}`,
        {
          method: 'PATCH',
          headers: {
            ...(await this.getHeadersForMethod()),
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update route point: ${response.status} - ${errorText}`);
      }

      const updatedPoints = await response.json();
      
      if (!updatedPoints || updatedPoints.length === 0) {
        return {
          success: false,
          error: 'Route point not found or update failed'
        };
      }

      const updatedPoint = updatedPoints[0];
      
      // Convert back to our RoutePoint format
      const routePoint: RoutePoint = {
        id: updatedPoint.id,
        lat: updatedPoint.latitude,
        lng: updatedPoint.longitude,
        orderIndex: updatedPoint.sequence,
        address_street: updatedPoint.address_street,
        address_house_number: updatedPoint.address_house_number,
        address_city: updatedPoint.address_city,
        address_suburb: updatedPoint.address_suburb,
        address_country: updatedPoint.address_country,
        address_display_name: updatedPoint.address_display_name,
        address_raw_data: updatedPoint.address_raw_data
      };

      return {
        success: true,
        data: routePoint
      };
    } catch (error) {
      console.error('Failed to update route point in Supabase:', error);
      
      // Enhanced error handling
      await handleApiError(error as Error, 'supabase/route_points', 'update_route_point', {
        routeId,
        pointId,
        service: 'storage',
        serviceType: 'Route Point Storage',
        customMessage: `Failed to update route point: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update route point'
      };
    }
  }

  /**
   * Save full route data from routing API (upsert - insert or update if exists)
   */
  async saveFullRouteData(routeId: string, fullData: Omit<RouteFullData, 'id' | 'route_id' | 'created_at' | 'updated_at'>): Promise<StorageResult<RouteFullData>> {
    try {
      // First, check if full route data already exists for this route
      const existingCheck = await this.getFullRouteData(routeId);
      
      const dataToSave = {
        route_id: routeId,
        route_geometry: fullData.route_geometry,
        route_properties: fullData.route_properties || null,
        routing_engine: fullData.routing_engine || 'brouter',
        routing_profile: fullData.routing_profile,
        routing_version: fullData.routing_version || null,
        routing_timestamp: fullData.routing_timestamp || new Date().toISOString(),
        coordinates_count: fullData.coordinates_count || null,
        is_compressed: fullData.is_compressed || false,
        compression_algorithm: fullData.compression_algorithm || null
      };

      let response;
      
      if (existingCheck.success && existingCheck.data) {
        // Update existing record
        response = await fetch(`${this.baseUrl}/rest/v1/route_full_data?route_id=eq.${routeId}`, {
          method: 'PATCH',
          headers: {
            ...(await this.getHeadersForMethod()),
            'Prefer': 'return=representation'
          },
          body: JSON.stringify({
            ...dataToSave,
            updated_at: new Date().toISOString()
          })
        });
      } else {
        // Insert new record
        response = await fetch(`${this.baseUrl}/rest/v1/route_full_data`, {
          method: 'POST',
          headers: {
            ...(await this.getHeadersForMethod()),
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(dataToSave)
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save full route data: ${response.status} - ${errorText}`);
      }

      const savedData = await response.json();
      const saved = savedData[0]; // Supabase returns an array with 'return=representation'

      const result: RouteFullData = {
        id: saved.id,
        route_id: saved.route_id,
        route_geometry: saved.route_geometry,
        route_properties: saved.route_properties,
        routing_engine: saved.routing_engine,
        routing_profile: saved.routing_profile,
        routing_version: saved.routing_version,
        routing_timestamp: saved.routing_timestamp ? new Date(saved.routing_timestamp) : undefined,
        coordinates_count: saved.coordinates_count,
        is_compressed: saved.is_compressed,
        compression_algorithm: saved.compression_algorithm,
        created_at: new Date(saved.created_at),
        updated_at: new Date(saved.updated_at)
      };

      return {
        success: true,
        data: result
      };
    } catch (error) {
      console.error('Failed to save full route data to Supabase:', error);
      
      // Enhanced error handling
      const errorCode = this.getStorageErrorCode(error, 'save');
      await handleApiError(error as Error, 'supabase/route_full_data', 'save_full_route_data', {
        routeId,
        coordinatesCount: fullData.coordinates_count,
        service: 'storage',
        serviceType: 'Full Route Data Storage',
        customMessage: this.getStorageErrorMessage(error, 'save'),
        errorCode
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save full route data'
      };
    }
  }

  /**
   * Get full route data for a specific route
   */
  async getFullRouteData(routeId: string): Promise<StorageResult<RouteFullData | null>> {
    try {
      const response = await fetch(
        `${this.baseUrl}/rest/v1/route_full_data?route_id=eq.${routeId}`,
        {
          headers: await this.getHeadersForMethod()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data || data.length === 0) {
        return {
          success: true,
          data: null // No full route data found
        };
      }

      const fullData = data[0]; // Should be only one record due to unique constraint
      
      const result: RouteFullData = {
        id: fullData.id,
        route_id: fullData.route_id,
        route_geometry: fullData.route_geometry,
        route_properties: fullData.route_properties,
        routing_engine: fullData.routing_engine,
        routing_profile: fullData.routing_profile,
        routing_version: fullData.routing_version,
        routing_timestamp: fullData.routing_timestamp ? new Date(fullData.routing_timestamp) : undefined,
        coordinates_count: fullData.coordinates_count,
        is_compressed: fullData.is_compressed,
        compression_algorithm: fullData.compression_algorithm,
        created_at: fullData.created_at ? new Date(fullData.created_at) : undefined,
        updated_at: fullData.updated_at ? new Date(fullData.updated_at) : undefined
      };

      return {
        success: true,
        data: result
      };
    } catch (error) {
      console.error('Failed to get full route data from Supabase:', error);
      
      // Enhanced error handling
      const errorCode = this.getStorageErrorCode(error, 'load');
      await handleApiError(error as Error, 'supabase/route_full_data', 'get_full_route_data', {
        routeId,
        service: 'storage',
        serviceType: 'Full Route Data Storage',
        customMessage: this.getStorageErrorMessage(error, 'load'),
        errorCode
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load full route data'
      };
    }
  }

  /**
   * Update full route data
   */
  async updateFullRouteData(routeId: string, fullData: Partial<RouteFullData>): Promise<StorageResult<RouteFullData>> {
    try {
      const updateData: any = {};
      
      if (fullData.route_geometry !== undefined) updateData.route_geometry = fullData.route_geometry;
      if (fullData.route_properties !== undefined) updateData.route_properties = fullData.route_properties;
      if (fullData.routing_engine !== undefined) updateData.routing_engine = fullData.routing_engine;
      if (fullData.routing_profile !== undefined) updateData.routing_profile = fullData.routing_profile;
      if (fullData.routing_version !== undefined) updateData.routing_version = fullData.routing_version;
      if (fullData.routing_timestamp !== undefined) updateData.routing_timestamp = fullData.routing_timestamp?.toISOString();
      if (fullData.coordinates_count !== undefined) updateData.coordinates_count = fullData.coordinates_count;
      if (fullData.is_compressed !== undefined) updateData.is_compressed = fullData.is_compressed;
      if (fullData.compression_algorithm !== undefined) updateData.compression_algorithm = fullData.compression_algorithm;

      const response = await fetch(
        `${this.baseUrl}/rest/v1/route_full_data?route_id=eq.${routeId}`,
        {
          method: 'PATCH',
          headers: {
            ...(await this.getHeadersForMethod()),
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update full route data: ${response.status} - ${errorText}`);
      }

      const updatedData = await response.json();
      
      if (!updatedData || updatedData.length === 0) {
        throw new Error('Full route data not found for update');
      }

      const updated = updatedData[0];
      
      const result: RouteFullData = {
        id: updated.id,
        route_id: updated.route_id,
        route_geometry: updated.route_geometry,
        route_properties: updated.route_properties,
        routing_engine: updated.routing_engine,
        routing_profile: updated.routing_profile,
        routing_version: updated.routing_version,
        routing_timestamp: updated.routing_timestamp ? new Date(updated.routing_timestamp) : undefined,
        coordinates_count: updated.coordinates_count,
        is_compressed: updated.is_compressed,
        compression_algorithm: updated.compression_algorithm,
        created_at: updated.created_at ? new Date(updated.created_at) : undefined,
        updated_at: updated.updated_at ? new Date(updated.updated_at) : undefined
      };

      return {
        success: true,
        data: result
      };
    } catch (error) {
      console.error('Failed to update full route data in Supabase:', error);
      
      // Enhanced error handling
      const errorCode = this.getStorageErrorCode(error, 'update');
      await handleApiError(error as Error, 'supabase/route_full_data', 'update_full_route_data', {
        routeId,
        service: 'storage',
        serviceType: 'Full Route Data Storage',
        customMessage: this.getStorageErrorMessage(error, 'update'),
        errorCode
      });
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update full route data'
      };
    }
  }
}
